// The running counters panel (ADR-0032; spec/13 §10) on Shifts, when several counters are open (2026-10-08: no longer on Sell): each counter open or free, who is on its
// drawer (avatars for a shared one), since when, its float, the cash taken so far and the expected drawer now — and on a
// shared drawer what each person took (cash, sales). A counter opens its shift's cash timeline (F-OPS-04).
//
// Managers (`shifts:read_all`) see every counter; staff see the drawer they are on. The server says which money the
// caller may see (`figuresVisible`), read from the same ledger lines as the timeline. It stays current by refetching
// every 20 seconds, on every shift change made here, and on the venue's orders channel (a sale paid moves the cash).

import type { Counter, RunningDrawer } from '@clavian/api-client';
import { channels, ORDERS_CREATED, ORDERS_UPDATED } from '@clavian/contracts/constants';
import type { TypeId } from '@clavian/contracts/typeid';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Avatar, Badge, Button, cx, Dialog } from '@clavian/ui-web';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import type { MyVenue } from '../../api/me';
import { useChannelEvents, useRealtimeLive } from '../../realtime';
import { Section } from '../../ui';
import { CashTimeline } from './CashTimeline';
import { shiftKeys, useCounters } from './queries';

import './running-counters.css';

const SHIFTS_OWN = 'shifts:own';
const SHIFTS_READ_ALL = 'shifts:read_all';
const ORDERS_READ = 'orders:read';

interface Row {
  readonly key: string;
  readonly name: string | null;
  readonly running: RunningDrawer | null;
}

export function RunningCounters({
  venue,
  inlineShiftId,
  title,
  actions,
  minRows = 1,
}: {
  readonly venue: Pick<MyVenue, 'id' | 'organizationId' | 'slug'>;
  /** A shift whose cash timeline the page already shows under the panel (Shifts): its card does not link to it again. */
  readonly inlineShiftId?: string | undefined;
  /** The panel's heading; "Counters" by default. */
  readonly title?: string;
  /** What the caller may do with an open counter's drawer (take it over, count and close it), by its shift. */
  readonly actions?: (shiftId: string) => ReactNode;
  /** Show the panel only when it lists at least this many counters (Shifts: two, so a one-counter venue stays simple). */
  readonly minRows?: number;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const access = useAccess({ venueId: venue.id });
  const queryClient = useQueryClient();
  const canOwn = access.status === 'ready' && access.can(SHIFTS_OWN);
  const readAll = access.status === 'ready' && access.can(SHIFTS_READ_ALL);
  const board = useCounters(venue.organizationId, venue.id, { enabled: canOwn, live: true });
  const live = useRealtimeLive() && canOwn && access.status === 'ready' && access.can(ORDERS_READ);
  const [open, setOpen] = useState<RunningDrawer | null>(null);

  useChannelEvents(
    channels.venue(venue.id as TypeId<'ven'>, 'orders'),
    (envelope) => {
      if (envelope.type === ORDERS_CREATED || envelope.type === ORDERS_UPDATED) void queryClient.invalidateQueries({ queryKey: shiftKeys.counters(venue.organizationId, venue.id) });
    },
    live,
  );

  if (!canOwn || board.data === undefined) return null;
  const me = access.status === 'ready' ? access.memberId : null;
  const mine = (running: RunningDrawer | null) => running !== null && running.members.some((member) => member.memberId === me && member.leftAt === null);
  const rows: Row[] = [
    ...board.data.counters.map((counter: Counter) => ({ key: counter.id, name: counter.name, running: counter.running })),
    ...board.data.unassigned.map((running) => ({ key: running.shiftId, name: running.counterName, running })),
  ].filter((row) => readAll || mine(row.running));
  if (rows.length === 0 || rows.length < minRows) return null;

  return (
    <Section title={title ?? t('shifts.counters.title')} titleAs="h3" description={readAll ? t('shifts.counters.introAll') : t('shifts.counters.introOwn')} body="rows" className="console-counters">
      <ul className="console-counters__list">
        {rows.map((row) => {
          const running = row.running;
          const active = running === null ? [] : running.members.filter((member) => member.leftAt === null);
          return (
            // An open counter carries the rail of a drawer in use (ADR-0043); a free one is plain.
            <li key={row.key} className={cx('console-counters__row', 'console-rail', running !== null && 'is-open')} data-rail={running === null ? undefined : 'ok'}>
              <div className="console-counters__head">
                <strong className="console-counters__name">{row.name ?? t('shifts.noCounter')}</strong>
                <Badge tone={running === null ? 'neutral' : 'ok'}>{running === null ? t('shifts.counters.free') : t('shifts.counters.open')}</Badge>
              </div>
              {running === null ? (
                <p className="console-subtle">{t('shifts.counters.nobody')}</p>
              ) : (
                <>
                  <div className="console-counters__who">
                    <span className="console-counters__avatars" aria-hidden="true">
                      {active.map((member) => (
                        <Avatar key={member.memberId} name={member.name ?? '?'} size="sm" decorative />
                      ))}
                    </span>
                    <span>
                      {t('shifts.counters.since', {
                        names: active.map((member) => member.name ?? t('shifts.holderUnknown')).join(', '),
                        time: format.time(running.openedAt),
                      })}
                    </span>
                  </div>
                  {running.figuresVisible ? (
                    <dl className="console-counters__figures">
                      <div>
                        <dt>{t('shifts.counters.float')}</dt>
                        <dd className="console-num">{running.openingFloat === null ? '—' : format.money(running.openingFloat)}</dd>
                      </div>
                      <div>
                        <dt>{t('shifts.counters.cashSoFar')}</dt>
                        <dd className="console-num">{running.cashSoFar === null ? '—' : format.money(running.cashSoFar)}</dd>
                      </div>
                      <div>
                        <dt>{t('shifts.counters.expected')}</dt>
                        <dd className="console-num">{running.expectedDrawer === null ? '—' : format.money(running.expectedDrawer)}</dd>
                      </div>
                    </dl>
                  ) : null}
                  {running.figuresVisible && running.members.length > 1 ? (
                    <table className="console-counters__people">
                      <caption className="clav-visually-hidden">{t('shifts.counters.byPerson', { counter: row.name ?? t('shifts.noCounter') })}</caption>
                      <thead>
                        <tr>
                          <th scope="col">{t('shifts.counters.person')}</th>
                          <th scope="col">{t('shifts.counters.cash')}</th>
                          <th scope="col">{t('shifts.counters.sales')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {running.members.map((member) => (
                          <tr key={`${member.memberId}-${member.joinedAt}`}>
                            <th scope="row">{member.name ?? t('shifts.holderUnknown')}</th>
                            <td className="console-num">{member.cash === null ? '—' : format.money(member.cash)}</td>
                            <td className="console-num">{member.salesCount ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                  <div className="console-counters__actions">
                    {running.shiftId === inlineShiftId ? null : actions?.(running.shiftId)}
                    {running.figuresVisible && running.shiftId !== inlineShiftId ? (
                      <Button variant="ghost" size="sm" onClick={() => setOpen(running)}>
                        {t('shifts.counters.timeline', { counter: row.name ?? t('shifts.noCounter') })}
                      </Button>
                    ) : null}
                  </div>
                </>
              )}
            </li>
          );
        })}
      </ul>
      <Dialog open={open !== null} onOpenChange={(next) => (next ? undefined : setOpen(null))} size="xl" title={t('shifts.counters.timelineTitle', { counter: open?.counterName ?? t('shifts.noCounter') })}>
        {open !== null && open.expectedDrawer !== null ? (
          <CashTimeline
            organizationId={venue.organizationId}
            venueId={venue.id}
            venueSlug={venue.slug}
            shiftId={open.shiftId}
            expectedCash={open.expectedDrawer}
            closed={false}
            canOpenOrders={access.status === 'ready' && access.can(ORDERS_READ)}
          />
        ) : null}
      </Dialog>
    </Section>
  );
}
